/**
 * ProductTour — جَولَة تَفاعُليّة لِلمُستَخدِم الجَديد (5 خُطوات).
 *
 * كَيفَ تَعمَل؟
 *   - تُشَغَّل تِلقائيّاً مَرَّةً واحِدَةً عَلى الأَكثَر (بَعد تَسجيل الدُّخول الأَوَّل)
 *     ما لَم يَكُن المُستَخدِم قَد أَكمَلَها أو تَخَطّاها (sessionStorage + localStorage).
 *   - يُمكِن إِعادَة تَشغيلها يَدَويّاً مِن دَليل الاِستِخدام عَبر زِرّ
 *     «أَعِد الجَولَة التَّعريفيّة».
 *   - تَحديد العُنصُر المُستَهدَف يَتِمّ بِـ `data-testid` (مُستَقِرّ، لا يَكسِر مَع إعادَة
 *     تَنسيق CSS). إن لَم يُوجَد العُنصُر (مَثَلاً عَلى صَفحَة غَير مَوجود فيها)،
 *     تُتَخَطّى الخُطوَة بِصَمت.
 *   - تَصميم بَدون مُكَتَبَة خارِجيّة — مَجَرَّد <div> ثابِت + قِناع SVG لِلتَّعتيم.
 *
 * كَيفَ تَستَخدِمها في App.js؟
 *   <ProductTour />  — مَرَّةً واحِدَةً، تَختار وَقت الإِشعال بِنَفسها.
 */
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, X, Sparkle } from "@phosphor-icons/react";
import { useUser } from "../context/UserAuthContext";

const STORAGE_KEY = "lexiq.tour.completed.v1";
const FORCE_KEY = "lexiq.tour.force_show";

// تَعريف الخُطوات — كُلّ خُطوَة تُشير لِعُنصُر بِـtestid وَلَها عُنوان+نَصّ.
// `route` (اِختياريّ): إن وُجِد، يَتِمّ التَّنَقُّل إلَيه قَبل عَرض الخُطوَة.
const STEPS = [
  {
    id: "welcome",
    testid: null, // مُتَوَسِّطَة بِدون هَدَف
    route: "/",
    title: "مَرحَباً بِك في LexIQ 👋",
    body:
      "دَعنا نُريك أَهَمّ ميزات التَّطبيق في أَقَلّ مِن دَقيقَة. " +
      "يُمكِنُك تَخَطّي الجَولَة في أيّ وَقت، أو إِعادَتها لاحِقاً مِن دَليل الاِستِخدام.",
  },
  {
    id: "cases",
    testid: "bnav-cases",
    route: "/",
    title: "إدارَة قَضاياك",
    body:
      "هُنا تُنَظِّم كُلّ القَضايا الَّتي تَعمَل عَلَيها. أَضِف قَضيّة جَديدَة، " +
      "وَوَثِّق سَير الجَلسات في تَسلسُل زَمَنيّ مُرَتَّب.",
  },
  {
    id: "ai",
    testid: "bnav-ai-cta",
    route: "/",
    title: "أَدَوات الذَّكاء الاِصطِناعيّ",
    body:
      "اِضغَط الزِّرّ الذَّهَبيّ لِلوُصول إلى تَحليل القَضايا، صياغَة الوَثائِق، " +
      "وَالبَحث الذَّكيّ في قَرارات التَّمييز.",
  },
  {
    id: "messages",
    testid: "bnav-messages",
    route: "/",
    title: "مُحادَثات آمِنَة",
    body:
      "تَواصَل مَع موَكِّليك أو زُمَلائِك المُحامين عَبر مُحادَثات مُشَفَّرَة، " +
      "وَأَجرِ مُكالَمات صَوتيّة بِجَودَة عاليَة بِدون مُغادَرَة التَّطبيق.",
  },
  {
    id: "account",
    testid: "bnav-account",
    route: "/",
    title: "حِسابك وَإحصائيّاتك",
    body:
      "مِن هُنا تَصِل إلى إِحصائيّاتك، إنجازاتك، تَحَدّي الأُسبوع، " +
      "وَإعدادات الإشعارات وَالخُصوصيّة. اِضغَط لِتَستَكشِف لاحِقاً.",
  },
];

function getTargetRect(testid) {
  if (!testid) return null;
  const el = document.querySelector(`[data-testid="${testid}"]`);
  if (!el) return null;
  return el.getBoundingClientRect();
}

export default function ProductTour() {
  const { user, loading } = useUser();
  const location = useLocation();
  const navigate = useNavigate();

  const [active, setActive] = useState(false);
  const [step, setStep] = useState(0);
  const [rect, setRect] = useState(null);
  const [cardHeight, setCardHeight] = useState(260); // measured live via ref
  const cardRef = useRef(null);
  const lastNavRef = useRef(null);

  // قَرار الإِشعال التِّلقائيّ — مَرَّةً واحِدَةً، بَعد تَسجيل الدُّخول.
  useEffect(() => {
    if (loading || !user) return;
    let force = false;
    try {
      force = sessionStorage.getItem(FORCE_KEY) === "1";
      if (force) sessionStorage.removeItem(FORCE_KEY);
    } catch { /* private mode */ }
    if (force) {
      setStep(0);
      setActive(true);
      return;
    }
    try {
      if (localStorage.getItem(STORAGE_KEY) === "1") return;
    } catch { /* private mode → اِسمَح بِالعَرض */ }
    // لا تَختَطِف المُستَخدِم إذا فَتَحَ رابِطاً مُباشِراً (مَثَلاً /pricing
    // أو /account/subscription أو /messages/X). الجَولة التَّعريفيّة
    // مُصَمَّمَة لِبَدء جَلسَة عاديّة مِن الصَّفحَة الرَّئيسيّة، وَلَيس لِقَطع
    // تَدَفُّق مُستَخدِم يَعرِف ما يُريد. سَتَظهَر تِلقائيّاً المَرَّة القادِمَة
    // الَّتي يَدخُل فيها مِن "/".
    if (location.pathname !== "/") return;
    // أَخِّر قَليلاً لِيَكتَمِل تَحميل الواجِهَة (سيّما الـbottom nav)
    const t = setTimeout(() => setActive(true), 1200);
    return () => clearTimeout(t);
  }, [loading, user, location.pathname]);

  // تَخَطّي الجَولة بِزِرّ Escape — مُريحٌ لِمُراجِع Apple.
  useEffect(() => {
    if (!active) return;
    const onKey = (e) => {
      if (e.key === "Escape") {
        e.preventDefault();
        finish();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [active]); // eslint-disable-line react-hooks/exhaustive-deps

  // قِياس مَوقِع الهَدَف عِندَ كُلّ تَغيير في الخُطوَة أو التَّمرير.
  useLayoutEffect(() => {
    if (!active) return;
    const current = STEPS[step];
    if (!current) return;
    // اِنتَقِل لِلمَسار الصَّحيح إن لَزِم
    if (current.route && location.pathname !== current.route && lastNavRef.current !== current.route) {
      lastNavRef.current = current.route;
      navigate(current.route);
      return;
    }
    const r = getTargetRect(current.testid);
    setRect(r);
    const onResize = () => setRect(getTargetRect(current.testid));
    window.addEventListener("resize", onResize);
    window.addEventListener("scroll", onResize, { passive: true });
    return () => {
      window.removeEventListener("resize", onResize);
      window.removeEventListener("scroll", onResize);
    };
  }, [active, step, location.pathname, navigate]);

  // Live-measure the actual card height whenever content changes so
  // we can position it against the target WITHOUT overlap regardless
  // of translation length or viewport size. ResizeObserver > polling.
  useLayoutEffect(() => {
    if (!active || !cardRef.current) return;
    const el = cardRef.current;
    const measure = () => {
      const h = el.getBoundingClientRect().height;
      if (h > 0) setCardHeight(h);
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [active, step]);

  const finish = useCallback(() => {
    setActive(false);
    try {
      localStorage.setItem(STORAGE_KEY, "1");
    } catch { /* no-op */ }
  }, []);

  const next = () => {
    if (step >= STEPS.length - 1) finish();
    else setStep((s) => s + 1);
  };
  const prev = () => setStep((s) => Math.max(0, s - 1));

  if (!active) return null;
  const current = STEPS[step];
  if (!current) return null;

  // ── Card positioning ───────────────────────────────────────────
  // The previous implementation hard-coded a 200 px card height,
  // which caused overlap with the target on small screens (iPhone SE
  // etc.) whenever the translation ran long. We now:
  //   1. Measure the card live via ResizeObserver (see useLayoutEffect
  //      above → `cardHeight`).
  //   2. Prefer the side (above/below) that has the MOST free space,
  //      falling back to a vertically-centred card if neither side
  //      can fit the measured height.
  //   3. Always clamp inside the safe viewport (16 px top,
  //      env(safe-area-inset-bottom) + 16 px bottom).
  const PAD = 16;
  const cardStyle = (() => {
    // No target → centre the intro card.
    if (!rect) {
      return {
        left: "50%",
        top: "50%",
        transform: "translate(-50%, -50%)",
        maxWidth: "min(92vw, 420px)",
        maxHeight: "min(80vh, 520px)",
      };
    }
    const vh = window.innerHeight;
    const safeTop = 16;
    const safeBottom = 16;  // env() lives in CSS; JS reads window only
    const usableTop = rect.top - PAD - safeTop;          // space above
    const usableBottom = vh - rect.bottom - PAD - safeBottom;  // below
    const needed = cardHeight;

    let cardTop;
    if (usableTop >= needed) {
      // Fits above cleanly — position card so its bottom is just above the target.
      cardTop = rect.top - PAD - needed;
    } else if (usableBottom >= needed) {
      // Fits below cleanly.
      cardTop = rect.bottom + PAD;
    } else {
      // Neither side has enough room — centre vertically and let the
      // card scroll internally. Better than any overlap.
      cardTop = Math.max(safeTop, (vh - needed) / 2);
    }
    // Final clamp to keep card fully on-screen.
    cardTop = Math.max(
      safeTop,
      Math.min(cardTop, vh - safeBottom - needed),
    );
    return {
      left: "50%",
      top: `${cardTop}px`,
      transform: "translateX(-50%)",
      maxWidth: "min(92vw, 420px)",
      maxHeight: `${Math.min(vh - safeTop - safeBottom, 520)}px`,
    };
  })();

  return (
    <div
      className="fixed inset-0 z-[1000] font-tajawal"
      dir="rtl"
      data-testid="product-tour-overlay"
      role="dialog"
      aria-modal="true"
    >
      {/* قِناع SVG: مُستَطيل أَسوَد شَفّاف مَع فُتحَة فَوق الهَدَف (إن وُجِد) */}
      <svg className="absolute inset-0 w-full h-full pointer-events-auto" onClick={finish}>
        <defs>
          <mask id="tour-mask">
            <rect width="100%" height="100%" fill="white" />
            {rect && (
              <rect
                x={rect.left - 8}
                y={rect.top - 8}
                width={rect.width + 16}
                height={rect.height + 16}
                rx={16}
                ry={16}
                fill="black"
              />
            )}
          </mask>
        </defs>
        <rect
          width="100%"
          height="100%"
          fill="rgba(10, 25, 47, 0.72)"
          mask="url(#tour-mask)"
        />
      </svg>

      {/* إطار ذَهَبيّ حَول الهَدَف لِلتَّأكيد البَصَريّ */}
      {rect && (
        <div
          aria-hidden="true"
          className="absolute pointer-events-none rounded-2xl ring-2 ring-[#D4AF37] animate-pulse"
          style={{
            left: rect.left - 8,
            top: rect.top - 8,
            width: rect.width + 16,
            height: rect.height + 16,
            boxShadow: "0 0 0 9999px rgba(0,0,0,0)",
          }}
        />
      )}

      {/* بِطاقَة المَحتوى */}
      <div
        ref={cardRef}
        className="absolute bg-white rounded-[24px] shadow-[0_20px_50px_-12px_rgba(0,0,0,0.45)] border border-[#D4AF37]/40 flex flex-col overflow-hidden transition-[top] duration-200 ease-out"
        style={cardStyle}
        data-testid="product-tour-card"
      >
        {/* Scrollable body — grows to fill, but never pushes footer out */}
        <div className="flex-1 min-h-0 overflow-y-auto p-5 pb-4">
          <button
            type="button"
            onClick={finish}
            aria-label="تَخَطّي الجَولَة"
            data-testid="product-tour-skip"
            className="absolute top-3 start-3 w-7 h-7 rounded-full bg-[#FAFAFA] hover:bg-[#EAEAEA] flex items-center justify-center transition z-10"
          >
            <X size={14} weight="bold" color="#5A6470" />
          </button>

          <div className="flex items-center gap-2 mb-3">
            <div
              className="w-8 h-8 rounded-full flex items-center justify-center shrink-0"
              style={{ background: "rgba(212,175,55,0.18)" }}
            >
              <Sparkle size={16} weight="duotone" color="#B8860B" />
            </div>
            <div
              className="text-[11px] text-[#8A95A0] font-bold tabular-nums"
              dir="ltr"
            >
              {step + 1} / {STEPS.length}
            </div>
          </div>

          <h3 className="font-amiri text-[20px] text-[#0A192F] font-bold leading-tight mb-3">
            {current.title}
          </h3>
          <p className="text-[13.5px] text-[#3F4750] leading-[1.9]">
            {current.body}
          </p>
        </div>

        {/* Sticky footer — progress bar + nav buttons always visible */}
        <div className="shrink-0 border-t border-[#F0F0F0] bg-white px-5 pt-3 pb-4">
          <div className="mb-3 bg-[#F2F2F2] rounded-full overflow-hidden h-1">
            <div
              className="h-full rounded-full transition-all"
              style={{
                width: `${((step + 1) / STEPS.length) * 100}%`,
                background: "linear-gradient(90deg, #E6C463 0%, #D4AF37 100%)",
              }}
            />
          </div>
          <div className="flex items-center justify-between gap-2">
            <button
              type="button"
              onClick={prev}
              disabled={step === 0}
              data-testid="product-tour-prev"
              className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full bg-white border border-[#EAEAEA] text-[13px] font-bold text-[#0A192F] hover:bg-[#FAFAFA] disabled:opacity-40 disabled:cursor-not-allowed transition"
            >
              <ArrowRight size={14} weight="bold" />
              السّابِق
            </button>
            <button
              type="button"
              onClick={next}
              data-testid="product-tour-next"
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-[#0A192F] text-white text-[13px] font-bold hover:bg-[#D4AF37] hover:text-[#0A192F] transition active:scale-95"
            >
              {step >= STEPS.length - 1 ? "اِنتَهَيت" : "التّالي"}
              {step < STEPS.length - 1 && <ArrowLeft size={14} weight="bold" />}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/* مُساعِد عُموميّ — يُمكِن اِستِدعاؤه مِن دَليل الاِستِخدام لِإِعادَة تَشغيل الجَولَة. */
export function restartProductTour(navigate) {
  try {
    localStorage.removeItem(STORAGE_KEY);
    sessionStorage.setItem(FORCE_KEY, "1");
  } catch { /* no-op */ }
  // اِنتَقِل إلى الصَّفحَة الرَّئيسيّة حَتّى تَظهَر الـbottom nav.
  if (navigate) navigate("/");
}
